import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { NewsletterSource } from '@wanderlust/shared';
import { api } from '../../lib/api';
import { trackEvent } from '../../lib/analytics';
import { Button, Heading, Input } from '../ui';

interface NewsletterFormProps {
  /** Placement this form is rendered in. Reported with the analytics events as
   *  `placement` (never `source`, which GA reads as the session's traffic
   *  source) so we can tell which surface converts — never the subscriber's email. */
  source: NewsletterSource;
  /** 'dark' for dark backgrounds (footer, CTA sections), 'light' for light backgrounds */
  variant?: 'dark' | 'light';
  /**
   * 'stacked' is the narrow column used in the blog footer and under a post,
   * where the signup sits inside a column of its own. 'row' puts the copy and
   * the field side by side from `md` up, so a standalone band reads as a strip
   * rather than taking a full screen of height (the homepage).
   */
  layout?: 'stacked' | 'row';
}

type Status = 'idle' | 'loading' | 'success' | 'error';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function NewsletterForm({ source, variant = 'dark', layout = 'stacked' }: NewsletterFormProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [fieldError, setFieldError] = useState('');
  const honeypotRef = useRef('');

  useEffect(() => {
    trackEvent('newsletter-signup-view', { placement: source });
  }, [source]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setStatus('idle');
      setFieldError('Enter a valid email address.');
      return;
    }

    setFieldError('');
    setStatus('loading');
    trackEvent('newsletter-signup-submit', { placement: source });

    try {
      await api.subscribeNewsletter({
        email: trimmed,
        source,
        _hp_field: honeypotRef.current || undefined,
      });
      setStatus('success');
      setEmail('');
      trackEvent('newsletter-signup-success', { placement: source });
    } catch {
      setStatus('error');
    }
  };

  const isDark = variant === 'dark';
  const isRow = layout === 'row';
  const errorId = `newsletter-error-${source}`;
  // `error-fg` is a dark red tuned for light surfaces; on the navy footer it
  // falls under 3:1, so the dark variant uses the light end of the error ramp.
  const errorTextClass = isDark ? 'text-error-border' : 'text-error-fg';

  return (
    <div
      className={
        isRow
          ? 'w-full flex flex-col gap-5 text-center md:flex-row md:items-center md:justify-between md:gap-12 md:text-left'
          : 'w-full max-w-md text-center'
      }
    >
      <div className={isRow ? 'md:max-w-lg' : undefined}>
        {/* The row layout stands alone as a page section, so it takes an h2;
            the stacked one sits under a section heading and stays h3. */}
        <Heading
          level={isRow ? 2 : 3}
          size="h5"
          weight="regular"
          className={`${isRow ? 'text-2xl mb-1' : 'text-xl mb-2'} ${isDark ? 'text-white' : 'text-primary'}`}
        >
          A little wanderlust for your inbox
        </Heading>
        <p className={`text-sm ${isRow ? '' : 'mb-4'} ${isDark ? 'text-white/60' : 'text-neutral-600'}`}>
          Places worth the trip, things worth doing there, and useful ideas for planning your next one. About once a month.
        </p>
      </div>

      <div className={isRow ? 'w-full md:max-w-md md:shrink-0' : undefined}>
        {/* Persistent live region — announcing from a node that only appears on
            success is unreliable in screen readers. */}
        <p role="status" aria-live="polite" className="sr-only">
          {status === 'success' ? 'Subscribed. Check your inbox to confirm.' : ''}
        </p>

        {status === 'success' ? (
          <div className={isDark ? 'text-white/70' : 'text-neutral-600'}>
            <p className="text-sm font-medium text-accent">Check your inbox to confirm.</p>
            <p className="text-sm mt-1">
              We sent a confirmation link. Click it and you're on the list. Already subscribed? You're all set.
            </p>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} noValidate>
              {/* Honeypot — invisible to users */}
              <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                <Input
                  id={`newsletter-website-${source}`}
                  type="text"
                  name="website_url"
                  tabIndex={-1}
                  autoComplete="off"
                  onChange={(e) => { honeypotRef.current = e.target.value; }}
                />
              </div>

              {/* Stacks under ~640px so the field keeps a usable width on phones */}
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1">
                  <Input
                    id={`newsletter-email-${source}`}
                    size="sm"
                    type="email"
                    name="email"
                    aria-label="Email address"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (fieldError) setFieldError('');
                      if (status === 'error') setStatus('idle');
                    }}
                    placeholder="you@example.com"
                    disabled={status === 'loading'}
                    error={Boolean(fieldError)}
                    aria-describedby={fieldError ? errorId : undefined}
                    className={`rounded-lg disabled:opacity-50 px-4 py-2 text-sm ${
                      isDark
                        ? 'text-neutral-800 bg-white border-0'
                        : 'text-neutral-800 bg-white border border-neutral-200'
                    }`}
                  />
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={status === 'loading'}
                  className="w-full sm:w-auto px-4 py-2 text-sm"
                >
                  {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
                </Button>
              </div>
            </form>

            {fieldError && (
              <p id={errorId} role="alert" className={`text-sm mt-2 ${errorTextClass}`}>
                {fieldError}
              </p>
            )}
            {status === 'error' && (
              <p role="alert" className={`text-sm mt-2 ${errorTextClass}`}>
                Something went wrong. Please try again.
              </p>
            )}

            <p className={`text-xs ${isRow ? 'mt-2' : 'mt-4'} ${isDark ? 'text-white/60' : 'text-neutral-500'}`}>
              Unsubscribe anytime. No inbox clutter.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
